import { statusOf, statusColor } from "@/lib/otStatus";
import { Fan } from "lucide-react";

function Gauge({ tag, x, y }) {
  if (!tag) return null;
  const st = statusOf(tag);
  const color = statusColor[st];
  const pct = Math.max(0, Math.min(100, ((tag.value - (tag.min ?? 0)) / ((tag.max ?? 100) - (tag.min ?? 0))) * 100));
  return (
    <div className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: x, top: y }} data-testid={`gauge-${tag.tag_id}`}>
      <div className="bg-popover border rounded-sm px-2.5 py-1.5 min-w-[112px]" style={{ borderColor: color + "66" }}>
        <div className="font-mono text-[10px] text-muted-foreground">{tag.tag_id}</div>
        <div className="font-mono text-base font-semibold leading-tight" style={{ color }}>
          {tag.value}<span className="text-[10px] text-muted-foreground ml-1">{tag.unit}</span>
        </div>
        <div className="h-1 bg-secondary rounded-full mt-1 overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
        </div>
      </div>
    </div>
  );
}

function Equip({ tag, x, y, label }) {
  if (!tag) return null;
  const on = ["RUNNING", "OPEN"].includes(tag.value);
  const isPump = tag.tag_id.startsWith("PMP");
  const color = tag.value === "TRIGGERED" ? statusColor.critical : on ? statusColor.normal : statusColor.inactive;
  return (
    <div className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1" style={{ left: x, top: y }} data-testid={`equip-${tag.tag_id}`}>
      <div className="rounded-full border-2 flex items-center justify-center bg-popover"
        style={{ borderColor: color, width: isPump ? 46 : 36, height: isPump ? 46 : 36, boxShadow: on ? `0 0 14px ${color}55` : "none" }}>
        {isPump ? (
          <Fan className={`w-5 h-5 ${on ? "animate-spin" : ""}`} style={{ color, animationDuration: "1.4s" }} />
        ) : (
          <div className="w-3.5 h-3.5 rotate-45" style={{ background: on ? color : "transparent", border: `2px solid ${color}` }} />
        )}
      </div>
      <div className="font-mono text-[9px] text-muted-foreground whitespace-nowrap">{tag.tag_id}</div>
      <div className="font-mono text-[9px] whitespace-nowrap" style={{ color }}>{tag.value}</div>
    </div>
  );
}

function Pipe({ x, y, w, active, vertical }) {
  return (
    <div className="absolute" style={{ left: x, top: y, width: vertical ? 4 : w, height: vertical ? w : 4 }}>
      <div className="w-full h-full rounded-full" style={{ background: active ? "#1e3a5f" : "#1f1f24" }} />
      {active && <div className="absolute inset-0 rounded-full opacity-70" style={{
        background: vertical
          ? "repeating-linear-gradient(180deg,#007AFF 0 6px,transparent 6px 16px)"
          : "repeating-linear-gradient(90deg,#007AFF 0 6px,transparent 6px 16px)",
        animation: "flow 1s linear infinite",
      }} />}
    </div>
  );
}

export default function ProcessMimic({ tags }) {
  const t = Object.fromEntries(tags.map((x) => [x.tag_id, x]));
  const flowing = t["PMP-01"]?.value === "RUNNING" && t["XV-501"]?.value === "OPEN" && t["XV-502"]?.value === "OPEN" && t["ESD-01"]?.value !== "TRIGGERED";

  return (
    <div className="bg-card border border-border/60 rounded-md overflow-hidden" data-testid="process-mimic">
      <style>{`@keyframes flow { from { background-position: 0 0; } to { background-position: 22px 22px; } }`}</style>
      <div className="px-3 py-2 border-b border-border/60 bg-popover flex items-center justify-between">
        <span className="label-ot">Process Mimic · Crude Transmission Line</span>
        <span className="font-mono text-xs" style={{ color: flowing ? statusColor.normal : statusColor.inactive }}>
          {flowing ? "● FLOWING" : "○ NO FLOW"}
        </span>
      </div>
      <div className="relative w-full cr-grid" style={{ height: 420 }}>
        {/* main pipe path */}
        <Pipe x="8%" y="50%" w="22%" active={t["XV-501"]?.value === "OPEN"} />
        <Pipe x="30%" y="50%" w="22%" active={flowing} />
        <Pipe x="52%" y="50%" w="20%" active={flowing} />
        <Pipe x="72%" y="50%" w="20%" active={t["XV-502"]?.value === "OPEN" && flowing} />
        {/* tank drop */}
        <Pipe x="62%" y="22%" w="28%" active={flowing} vertical />

        {/* inlet label */}
        <div className="absolute font-mono text-[10px] text-muted-foreground -translate-y-1/2" style={{ left: "2%", top: "50%" }}>INLET ►</div>
        <div className="absolute font-mono text-[10px] text-muted-foreground -translate-y-1/2" style={{ right: "1%", top: "50%" }}>► OUT</div>

        {/* gauges */}
        <Gauge tag={t["PT-101"]} x="20%" y="26%" />
        <Gauge tag={t["FT-201"]} x="42%" y="74%" />
        <Gauge tag={t["PT-102"]} x="82%" y="26%" />
        <Gauge tag={t["TT-301"]} x="62%" y="74%" />

        {/* surge tank */}
        <div className="absolute -translate-x-1/2" style={{ left: "62%", top: "8%" }} data-testid="surge-tank">
          {t["LT-401"] && (() => {
            const lvl = t["LT-401"].value;
            const st = statusOf(t["LT-401"]);
            return (
              <div className="flex flex-col items-center">
                <div className="w-16 h-14 border-2 border-border rounded-sm relative overflow-hidden bg-popover">
                  <div className="absolute bottom-0 left-0 w-full transition-all duration-500" style={{ height: `${lvl}%`, background: statusColor[st] + "55", borderTop: `2px solid ${statusColor[st]}` }} />
                  <div className="absolute inset-0 flex items-center justify-center font-mono text-[11px] font-semibold">{lvl}%</div>
                </div>
                <div className="font-mono text-[9px] text-muted-foreground mt-0.5">LT-401 TANK</div>
              </div>
            );
          })()}
        </div>

        {/* equipment */}
        <Equip tag={t["XV-501"]} x="20%" y="50%" />
        <Equip tag={t["PMP-01"]} x="40%" y="50%" />
        <Equip tag={t["PMP-02"]} x="55%" y="50%" />
        <Equip tag={t["XV-502"]} x="82%" y="50%" />
      </div>
    </div>
  );
}
